import {
  Box,
  Button,
  Center,
  Divider,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Spacer,
  Spinner,
  Stack,
  TableProps,
  Tag,
  Text,
  Tooltip,
  useColorModeValue,
  useMenuContext,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { LineItemType } from '@pelicargo/types';
import { copyText, makeBasicTable, Show } from '@pelicargo/ui';
import {
  allInVariableRate,
  calculateDistance,
  getPrettyLineItemType,
  kmToMi,
  lineItemTypeOptions,
  prettyCurrency,
  variantTypeOptions,
} from '@pelicargo/utils';
import { Provider, QuoteSource } from '@prisma/client';
import { MouseEventHandler, useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HiClipboardCopy, HiOutlineCloudDownload, HiOutlineCloudUpload } from 'react-icons/hi';
import {
  HiArrowPathRoundedSquare,
  HiBoltSlash,
  HiMiniTrash,
  HiOutlineMinusCircle,
  HiOutlinePlusCircle,
} from 'react-icons/hi2';
import { IoAirplane } from 'react-icons/io5';
import { Link, useLocation } from 'react-router-dom';

import { trpc } from '../../config/trpc';
import { useCreateLineItem } from '../../hooks/lineItem/useCreate';
import { useLineItems } from '../../hooks/lineItem/useLineItems';
import { useBooking } from '../../hooks/quoteVariant/useBooking';
import { useDeleteQuoteVariant } from '../../hooks/quoteVariant/useDelete';
import { useQuoteVariantForm } from '../../hooks/quoteVariant/useForm';
import { useUpdateQuoteVariant } from '../../hooks/quoteVariant/useUpdate';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { Quote, QuoteLineItem, QuoteVariantByQuoteId } from '../../utils/types';
import { ActionMenu } from '../ActionMenu';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { OptionGroupOption, SimpleSelect } from '../SimpleSelect';
import { SimplePopoverUnifiedInput } from '../SimpleUnifiedPopoverInput';
import { LineItemRow } from './LineItemRow';
import { QuoteVariantFlightPath } from './QuoteVariantFlightPath';

type Props = TableProps & {
  quoteVariant: QuoteVariantByQuoteId;
  quote: Quote & { provider?: Provider };
  isBooked?: boolean;
};

const RenderLineItemType = ({
  option,
  qvId,
  exists,
}: {
  option: OptionGroupOption['options'][0];
  qvId: number;
  exists: boolean;
}) => {
  const { onClose } = useMenuContext();
  const { createDefault: createLineItem } = useCreateLineItem(qvId);

  const handleOnClick = useCallback<MouseEventHandler>(
    async (e) => {
      await createLineItem({ label: getPrettyLineItemType(option.value as LineItemType), type: option.value });
      if (!e.shiftKey) onClose();
    },
    [createLineItem, onClose, option.value],
  );

  return (
    <MenuItem key={option.value} onClick={handleOnClick} py="1" fontSize="md" command={exists ? '✓' : undefined}>
      {option.label}
    </MenuItem>
  );
};

export const QuoteVariantTable = ({ quoteVariant, quote, isBooked = false }: Props) => {
  const toast = useToast();

  const { request, refetchRequest } = useCurrentRequest();
  const { groupedLineItems, totalCost, isLoading, lineItems } = useLineItems(quoteVariant?.id);
  const inheritedInlandQuoteVariant = quoteVariant.inland_service_quote_variant;
  const { data: pulledInlandQuoteVariant } = trpc.getInlandQuoteVariant.useQuery(
    { id: quoteVariant?.inland_service_quote_variant_id },
    { enabled: !!quoteVariant?.inland_service_quote_variant_id && !inheritedInlandQuoteVariant },
  );
  const inlandQuoteVariant = inheritedInlandQuoteVariant || pulledInlandQuoteVariant;
  const inlandLineItems = useLineItems(inlandQuoteVariant?.id);
  const [updateQuoteVariant] = useUpdateQuoteVariant(quoteVariant?.id);
  const [deleteQuoteVariant, isDeleting] = useDeleteQuoteVariant(quoteVariant?.id);
  const { mutateAsync: disconnectInlandQuoteVariant, isLoading: isDisconnecting } =
    trpc.disconnectInlandQuoteVariant.useMutation();

  const {
    isLoading: isBookingLoading,
    createBooking,
    switchBooking,
    cancelBooking,
    updateBooking,
    refreshBooking,
  } = useBooking();

  const { mutateAsync: bookInlandApi, isLoading: isBookingInlandApi } = trpc.bookQuoteVariant.useMutation();
  const { mutateAsync: refreshInlandApi, isLoading: isRefreshingInlandApi } =
    trpc.refreshInlandApiBookedQuoteVariant.useMutation();
  const { mutateAsync: updateInlandApi, isLoading: isUpdatingInlandApi } =
    trpc.updateInlandApiBookedQuoteVariant.useMutation();
  const { mutateAsync: clearInlandApi, isLoading: isClearingInlandApi } =
    trpc.clearInlandApiQuoteVariantBooking.useMutation();
  const isInlandApiLoading = isBookingInlandApi || isRefreshingInlandApi || isUpdatingInlandApi || isClearingInlandApi;

  const { methods, defaultValues } = useQuoteVariantForm(quoteVariant);

  const finalRate = useMemo(() => {
    if (!request?.chargeable_weight) return 0;
    return Number(totalCost + inlandLineItems?.totalCost || 0) / request.chargeable_weight;
  }, [request.chargeable_weight, totalCost, inlandLineItems?.totalCost]);

  const bgColor = useColorModeValue('white', 'gray.600');
  const color = useColorModeValue('gray.700', 'white');
  const { hash } = useLocation();

  const handleValid = async (fields: Record<any, string>) => updateQuoteVariant?.(fields);

  const renderLineItem = (lineItem: QuoteLineItem) => (
    <Show if={!!lineItem?.id} key={`line-item-${lineItem.id}`}>
      <LineItemRow lineItem={lineItem} isDisabled={lineItem.is_bundle} />
      {lineItem.children?.map((child) => renderLineItem(child))}
    </Show>
  );

  const bgLabel = useColorModeValue('gray.300', 'gray.800');
  const bgHeader = useColorModeValue('gray.50', 'gray.700');
  const connectedLineItemBgColorOnHover = useColorModeValue('gray.200', 'whiteAlpha.100');
  const isInland = !!quoteVariant?.as_request_inland_quote_variant_id;
  const isSystemSourcedQuote = quote?.sourced_by === QuoteSource.SYSTEM;
  const isInlandApiQv = isInland && !!quote?.is_api_generated;

  const bookedQuoteVariantId = useMemo(
    () => request?.booked_quote_variant_id || request?.booked_quote_variant?.id,
    [request?.booked_quote_variant?.id, request?.booked_quote_variant_id],
  );

  const isBookedVariantInCurrentQuote = useMemo(() => {
    const bookedVariantQuoteId = request?.booked_quote_variant?.quote_id || request?.booked_quote_variant?.quote?.id;
    if (!bookedVariantQuoteId || !quoteVariant?.quote_id) return false;

    return bookedVariantQuoteId === quoteVariant.quote_id;
  }, [quoteVariant?.quote_id, request?.booked_quote_variant?.quote?.id, request?.booked_quote_variant?.quote_id]);

  const isVariantBooked = useMemo(() => {
    return isBooked || quoteVariant?.id === bookedQuoteVariantId || (isInlandApiQv && !!quote?.integration_booking_id);
  }, [bookedQuoteVariantId, isBooked, isInlandApiQv, quoteVariant?.id, quote?.integration_booking_id]);

  const borderColor = isVariantBooked
    ? 'blue.500'
    : hash.replace('#QV', '') === String(quoteVariant?.id)
      ? 'primary.500'
      : 'border.default';
  const doesBookingExist = useMemo(
    () => !!bookedQuoteVariantId && isBookedVariantInCurrentQuote,
    [bookedQuoteVariantId, isBookedVariantInCurrentQuote],
  );

  const handleErrors = useMemo(
    () => async (fn: () => Promise<any>) => {
      try {
        await fn();
      } catch (e) {
        toast({ colorScheme: 'red', description: e?.message });
      }
    },
    [toast],
  );

  const handleSwitchBooking = useCallback(async () => {
    await switchBooking(quoteVariant);
    toast({ colorScheme: 'green', title: 'Booking switched', description: quoteVariant?.name });
    void refetchRequest();
  }, [quoteVariant, refetchRequest, switchBooking, toast]);

  const bookQuoteVariantButtonConfigs = useMemo(() => {
    if (isVariantBooked) {
      return [
        ...(!isSystemSourcedQuote
          ? [
              {
                label: 'Refresh Booking',
                icon: <HiOutlineCloudDownload />,
                onClick: () => handleErrors(() => refreshBooking(quoteVariant)),
              },
              {
                label: 'Update Booking',
                icon: <HiOutlineCloudUpload />,
                onClick: () => handleErrors(() => updateBooking(quoteVariant)),
              },
            ]
          : []),
        {
          label: 'Clear Booking',
          icon: <HiOutlineMinusCircle />,
          onClick: () => handleErrors(() => cancelBooking(quoteVariant)),
        },
      ];
    }
    if (doesBookingExist) {
      return [
        {
          label: 'Switch Booking',
          icon: <HiArrowPathRoundedSquare />,
          onClick: () => handleErrors(handleSwitchBooking),
        },
      ];
    }

    return [
      {
        label: 'Book Variant' + (quote.is_api_generated && quote?.provider?.has_api_booking ? ' via API' : ''),
        icon: <IoAirplane />,
        onClick: () => handleErrors(() => createBooking(quoteVariant)),
      },
    ];
  }, [
    isVariantBooked,
    doesBookingExist,
    quote?.is_api_generated,
    quote?.provider?.has_api_booking,
    isSystemSourcedQuote,
    handleErrors,
    refreshBooking,
    quoteVariant,
    updateBooking,
    cancelBooking,
    handleSwitchBooking,
    createBooking,
  ]);

  const renderLineItemTypeOption = useCallback(
    (option: OptionGroupOption) => (
      <MenuGroup title={option.groupLabel} my="1" key={option.groupLabel}>
        {option.options.map((opt) => (
          <RenderLineItemType
            key={opt.value}
            qvId={quoteVariant.id}
            option={opt}
            exists={!!lineItems.find((l) => l.type === opt.value)}
          />
        ))}
      </MenuGroup>
    ),
    [lineItems, quoteVariant.id],
  );

  const handleDisconnectInlandQuoteVariant = async (inland_quote_variant_id: number) => {
    await disconnectInlandQuoteVariant({ inland_quote_variant_id, quote_variant_id: quoteVariant.id });
  };

  const linkProps = { as: Link, to: `/requests/${request.id}/inland#QV${inlandQuoteVariant?.id}` };

  const hasFaBooking = isInlandApiQv && (isVariantBooked || !!quote?.integration_booking_id);

  const inlandApiBookingButtonConfigs = useMemo(() => {
    if (hasFaBooking) {
      return [
        ...(!isSystemSourcedQuote
          ? [
              {
                label: 'Refresh Booking',
                icon: <HiOutlineCloudDownload />,
                onClick: () => handleErrors(() => refreshInlandApi({ id: quoteVariant.id })),
              },
              {
                label: 'Update Booking',
                icon: <HiOutlineCloudUpload />,
                onClick: () => handleErrors(() => updateInlandApi({ id: quoteVariant.id })),
              },
            ]
          : []),
        {
          label: 'Cancel Booking',
          icon: <HiOutlineMinusCircle />,
          onClick: () => handleErrors(() => clearInlandApi({ id: quoteVariant.id })),
        },
      ];
    }
    return [
      {
        label: 'Book Variant',
        icon: <IoAirplane />,
        onClick: () => handleErrors(() => bookInlandApi({ id: quoteVariant.id })),
      },
    ];
  }, [
    hasFaBooking,
    isSystemSourcedQuote,
    handleErrors,
    quoteVariant.id,
    bookInlandApi,
    refreshInlandApi,
    updateInlandApi,
    clearInlandApi,
  ]);

  const isDeleteDisabled = isInland && !!quoteVariant?.quote_variants_for_inland_service?.length;
  const actions = isInlandApiQv
    ? [
        ...inlandApiBookingButtonConfigs,
        {
          label: 'Delete',
          color: 'error',
          icon: <HiMiniTrash />,
          isDisabled: isDeleteDisabled,
          tooltip: isDeleteDisabled ? 'Disconnect all inland quote variants in order to deleting this' : '',
          onClick: deleteQuoteVariant,
        },
      ]
    : isInland
      ? [
          {
            label: 'Delete',
            color: 'error',
            icon: <HiMiniTrash />,
            isDisabled: isDeleteDisabled,
            tooltip: isDeleteDisabled ? 'Disconnect all inland quote variants in order to deleting this' : '',
            onClick: deleteQuoteVariant,
          },
        ]
      : [
          ...bookQuoteVariantButtonConfigs,
          {
            label: 'Delete',
            color: 'error',
            icon: <HiMiniTrash />,
            onClick: deleteQuoteVariant,
          },
        ];

  const addressZip = request?.cargo_origin?.zip_code;
  const { data: zip_latlng } = trpc.getPostalCodeLocation.useQuery({ code: addressZip });
  const { data: airport } = trpc.getAirport.useQuery({ id: isInland ? quoteVariant?.name?.slice(0, 3) : '' });
  const distance = useMemo(
    () =>
      calculateDistance(
        { latitude: zip_latlng?.lat, longitude: zip_latlng?.lon },
        { latitude: airport?.latlng?.[0], longitude: airport?.latlng?.[1] },
      ),
    [zip_latlng, airport?.latlng],
  );

  const copyVariant = useCallback(() => {
    const allLineItems = [...groupedLineItems, ...(inlandLineItems?.lineItems || [])];

    const lines = allLineItems.map((li) => {
      let cost = li.cost;
      if (li.children) {
        cost += li.children.reduce((agg, child) => child.cost + agg, 0);
      }

      return [
        li.label,
        li.per_unit_cost_type === 'CURRENCY' && li.is_per_unit
          ? `${prettyCurrency(cost / (request?.chargeable_weight || 1), { isCents: false })}/kg`
          : '',
        prettyCurrency(cost, { isCents: false }),
      ];
    });

    lines.push(['', '', '']);
    lines.push([
      'Total',
      `${prettyCurrency(allInVariableRate(lineItems, request?.chargeable_weight), { isCents: false })}/kg`,
      prettyCurrency(totalCost + inlandLineItems?.totalCost, { isCents: false }),
    ]);

    const padded = makeBasicTable(lines);

    copyText(padded);
    toast({
      colorScheme: 'green',
      title: 'Copied!',
      description: '(In the same format as Customer)',
      duration: 2000,
    });
  }, [groupedLineItems, inlandLineItems, lineItems, request?.chargeable_weight, toast, totalCost]);

  return (
    <SimpleForm
      onValid={handleValid}
      defaultValues={defaultValues as QuoteVariantByQuoteId}
      {...(methods as UseFormReturn<any>)}
    >
      <VStack
        id={`quote-variant-${quoteVariant.id}`}
        borderRadius={8}
        borderWidth={1}
        outline={isVariantBooked ? 'solid' : 'transparent'}
        outlineColor={borderColor}
        borderColor={borderColor}
        spacing={0}
        overflow="hidden"
      >
        <HStack w="full" spacing={1} justifyContent="space-between" p={2} borderTopRadius="md" bg={bgLabel}>
          <SimplePopoverUnifiedInput names={['name', 'type']} joiner=" (" suffix=")">
            <SimpleInput name="name" label="Label" ignoreOnBlur />
            <SimpleSelect name="type" label="Type" ignoreOnBlur options={variantTypeOptions} />
            <Text fontSize="sm">{quoteVariant?.id}</Text>
          </SimplePopoverUnifiedInput>
          <Show if={isInland}>
            <Tooltip label="Open Calculator">
              <Button
                variant="ghost"
                fontSize="xs"
                onClick={() => {
                  window.open(`/calculator/zip ${addressZip}`, undefined, 'popup=true,width=800,height=600');
                }}
              >
                {`(${Math.round(kmToMi(distance))} mi)`}
              </Button>
            </Tooltip>
          </Show>
          <Spacer />
          <SimpleInput name="airline_ref" placeholder="Airline Ref #" size="sm" maxWidth="180px" />
          <Stack direction="row" spacing={0}>
            <IconButton aria-label="Copy" icon={<HiClipboardCopy />} variant="ghost" onClick={copyVariant} />
            <Stack direction="row">
              <Show if={isVariantBooked}>
                <Tag borderRadius="full" color="white" bg="blue.500" alignSelf="center">
                  Booked
                </Tag>
              </Show>
              <ActionMenu actions={actions} isLoading={isBookingLoading || isInlandApiLoading || isDeleting} />
            </Stack>
          </Stack>
        </HStack>
        <Stack w="full" spacing={0} bg={bgHeader}>
          <Grid templateColumns="repeat(28, 1fr)" w="full" gap={1} py={1}>
            <GridItem colSpan={1} />
            <GridItem colSpan={8}>
              <Text textStyle="label" px="2">
                Label
              </Text>
            </GridItem>
            <GridItem colSpan={6}>
              <Text textStyle="label" px="3.5">
                Type
              </Text>
            </GridItem>
            <GridItem colSpan={3}>
              <Text textStyle="label" align="right" px="1">
                Unit
              </Text>
            </GridItem>
            <GridItem colSpan={4}>
              <Text textStyle="label" align="right" px="1">
                Rate
              </Text>
            </GridItem>
            <GridItem colSpan={4}>
              <Text textStyle="label" align="right" px="1">
                Cost
              </Text>
            </GridItem>
            <GridItem colSpan={1} />
            <GridItem colSpan={1} />
          </Grid>
          <Stack w="full" spacing={0} bgColor={bgColor}>
            {groupedLineItems?.map(renderLineItem)}
            {inlandQuoteVariant?.id && (
              <Grid
                templateColumns="repeat(28, 1fr)"
                w="full"
                borderTopWidth={1}
                gap={0}
                bgColor={bgColor}
                className="line-item"
                _hover={{ bgColor: connectedLineItemBgColorOnHover }}
                _last={{ borderBottomWidth: 1 }}
              >
                <GridItem colSpan={1} {...linkProps}>
                  <Stack direction="row" align="center" justify="center" h="full" w="full" px={4} cursor="pointer">
                    <Box w={2} h={2} borderRadius="full" bgColor="success" borderWidth={1.5} borderColor="success" />
                  </Stack>
                </GridItem>
                <GridItem colSpan={21} alignContent="center" {...linkProps}>
                  <Input
                    name="label"
                    borderRadius={0}
                    borderWidth={0}
                    sx={{ '.line-item:hover &': { bg: 'transparent', opacity: 1 } }}
                    px="2"
                    value={inlandQuoteVariant.name}
                    pointerEvents="none"
                  />
                </GridItem>
                <GridItem colSpan={4} alignContent="center" {...linkProps}>
                  <Input
                    name="cost"
                    borderRadius={0}
                    borderWidth={0}
                    textAlign="right"
                    sx={{ '.line-item:hover &': { bg: 'transparent', opacity: 1 } }}
                    px="2"
                    value={inlandLineItems?.totalCost}
                    pointerEvents="none"
                  />
                </GridItem>
                <GridItem colSpan={1} />
                <GridItem colSpan={1} alignContent="center">
                  <Tooltip label="Disconnect">
                    <IconButton
                      aria-label="Disconnect"
                      icon={<HiBoltSlash />}
                      variant="ghost"
                      colorScheme="red"
                      opacity={isDisconnecting ? 1 : 0}
                      sx={{ '.line-item:hover &': { opacity: 1 } }}
                      isLoading={isDisconnecting}
                      onClick={() => handleDisconnectInlandQuoteVariant(inlandQuoteVariant.id)}
                    />
                  </Tooltip>
                </GridItem>
              </Grid>
            )}
          </Stack>
          <Show if={isLoading}>
            <Center w="full" py={4}>
              <Spinner size="sm" />
            </Center>
          </Show>
          <Grid templateColumns="repeat(28, 1fr)" w="full" gap={0} pl="4" py="2" bg={bgColor} alignItems="center">
            <GridItem colSpan={18}>
              <Stack direction="row">
                <Menu closeOnSelect={false}>
                  <MenuButton as={Button} variant="outline" leftIcon={<HiOutlinePlusCircle />}>
                    Add Charge
                  </MenuButton>
                  <MenuList>
                    <Stack divider={<Divider />} maxH="300px" overflowY="auto">
                      {lineItemTypeOptions.map(renderLineItemTypeOption)}
                    </Stack>
                  </MenuList>
                </Menu>
              </Stack>
            </GridItem>
            <GridItem colSpan={4}>
              <Text fontSize={{ base: 'sm', '2xl': 'lg' }} fontWeight="semibold" textAlign="right" color={color}>
                {prettyCurrency(finalRate, { isCents: false })}
              </Text>
            </GridItem>
            <GridItem colSpan={4}>
              <Text fontSize={{ base: 'sm', '2xl': 'lg' }} fontWeight="semibold" textAlign="right" color={color}>
                {prettyCurrency(totalCost + inlandLineItems?.totalCost || 0, { isCents: false })}
              </Text>
            </GridItem>
            <GridItem colSpan={5} />
            <GridItem colSpan={5} />
          </Grid>
          <Show if={!!quoteVariant?.flight_path?.length}>
            <QuoteVariantFlightPath flightSegments={quoteVariant?.flight_path} />
          </Show>
        </Stack>
      </VStack>
    </SimpleForm>
  );
};
